import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import {
  Alert,
  Badge,
  Button,
  Group,
  Heading,
  Spinner,
  Text,
} from '@apollo/orbit';
import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useRouteParams } from 'src/hooks/useRouteParams';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import {
  proposalHomepageRouteConfig,
  variantHomepageRouteConfig,
} from '../../routes';

export const OperationCollectionMarkdownElement = ({
  collection,
  loading,
}: {
  collection:
    | GraphQLTypes.OperationCollectionCollectionsReadmeQuery_service_variant_operationCollections
    | undefined;
  loading: boolean;
}) => {
  const { graphVisibilityType, graphId, graphVariant } = useRouteParams(
    variantHomepageRouteConfig,
    proposalHomepageRouteConfig,
  );

  const { locationToExplorer } = useInternalGraphLinking();

  if (loading) {
    return (
      <Container>
        <Header />
        <div className="flex items-center justify-between p-4">
          <Group gap={2}>
            <Spinner size="xs" />
            <Text as="span" variant="bodySb">
              Loading
            </Text>
          </Group>
          <Button
            variant="primary"
            isDisabled={true}
            size="sm"
            rightIcon={<IconOutlink />}
          >
            Open
          </Button>
        </div>
      </Container>
    );
  }
  if (!collection) {
    return (
      <Container>
        <Header />
        <div className="p-4">
          <Alert status="warning">Collection not found</Alert>
        </div>
      </Container>
    );
  }
  return (
    <Container>
      <Header />
      <div className="flex justify-between p-4">
        <div className="max-w-5/6">
          <Group gap={2}>
            <Heading as="span" className="truncate" title={collection.name}>
              {collection.name}
            </Heading>
            <Badge>{`${collection.operations.length} ${format.pluralize(
              collection.operations.length,
              'operation',
            )}`}</Badge>
          </Group>
          {collection.description && (
            <p
              title={collection.description}
              className="line-clamp-3 break-words text-base"
            >
              {collection.description}
            </p>
          )}
        </div>
        <Button
          size="sm"
          to={locationToExplorer({
            graphId,
            graphVariant,
            graphVisibilityType,
            savedCollectionId: collection.id,
            focusCollectionId: collection.id,
          })}
          target="_blank"
          as={Link}
          data-analytics-label="README content: Open collection in Explorer"
          data-analytics-category="Variant Home"
          rightIcon={<IconOutlink />}
        >
          Open
        </Button>
      </div>
    </Container>
  );
};

function Container({ children }: { children: ReactNode }) {
  return (
    <div className="my-4 overflow-hidden rounded border border-primary">
      {children}
    </div>
  );
}

function Header() {
  return (
    <div className="text-title rounded-t border-b border-primary bg-secondary py-2 pl-4">
      Operation collection
    </div>
  );
}
